import { useState } from 'react';
import { Phone, ExternalLink, Loader2, AlertCircle, Settings, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth-store';
import { useWhatsAppStore } from '@/stores/whatsapp-store';
import { useModuleStore } from '@/stores/module-store';
import type { WhatsAppModule } from '@/modules/tpa/whatsapp/whatsapp.module';

interface WhatsAppCallSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  senderPhoneNumberId: string;
  onEnabled?: () => void;
}

export function WhatsAppCallSettingsDialog({
  open,
  onOpenChange,
  senderPhoneNumberId,
  onEnabled,
}: WhatsAppCallSettingsDialogProps) {
  const [enabling, setEnabling] = useState(false);
  const [error, setError] = useState<{ title?: string; message?: string; details?: string; note?: string } | null>(null);
  const [success, setSuccess] = useState(false);

  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const isAdmin = !selectedAdminUserId;
  const callingEnabled = useWhatsAppStore((s) => s.callingEnabled[senderPhoneNumberId] ?? false);
  const tpaModule = useModuleStore((s) => s.tpaModule);

  // Resolve WABA ID for WhatsApp Manager link
  const wabaId = useWhatsAppStore((s) => s.getWabaIdForSenderId(senderPhoneNumberId));
  const whatsappManagerUrl = wabaId
    ? `https://business.facebook.com/latest/whatsapp_manager/phone_numbers?&asset_id=${wabaId}`
    : 'https://business.facebook.com/latest/whatsapp_manager/phone_numbers';

  const getWaModule = () => tpaModule as WhatsAppModule;

  const handleToggleCalling = async () => {
    if (!tpaModule) return;
    setError(null);
    setEnabling(true);

    try {
      const newEnabled = !callingEnabled;
      const settings = newEnabled
        ? { status: 'ENABLED' as const, call_icon_visibility: 'DEFAULT' as const, callback_permission_status: 'ENABLED' as const }
        : { status: 'DISABLED' as const };

      const result = await getWaModule().updateCallSettings(senderPhoneNumberId, settings);

      if (result.success) {
        if (newEnabled) {
          setSuccess(true);
          setTimeout(() => {
            onOpenChange(false);
            setSuccess(false);
            onEnabled?.();
          }, 800);
        }
      } else {
        setError({
          title: result.errorTitle,
          message: result.errorMessage,
          details: result.errorDetails,
          note: result.errorNote,
        });
      }
    } catch (err) {
      setError({ message: err instanceof Error ? err.message : 'An unexpected error occurred' });
    } finally {
      setEnabling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-green-600" />
            WhatsApp Calling
          </DialogTitle>
          <DialogDescription>
            {callingEnabled
              ? 'WhatsApp calling is enabled for this number.'
              : 'WhatsApp calling is not enabled for this number.'}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/40 p-3 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="space-y-1 min-w-0">
                {error.title && <p className="font-medium">{error.title}</p>}
                {error.message && <p>{error.message}</p>}
                {error.details && <p className="text-xs text-red-600/80 dark:text-red-400/80">{error.details}</p>}
                {error.note && <p className="text-xs font-medium text-red-700 dark:text-red-300 mt-1">{error.note}</p>}
                {!error.title && !error.message && !error.details && <p>Failed to update call settings.</p>}
              </div>
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="flex items-center gap-2 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800/40 p-3 text-sm text-green-700 dark:text-green-300">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span>WhatsApp calling enabled successfully!</span>
            </div>
          )}

          {/* Non-admin: simple message */}
          {!isAdmin && !callingEnabled && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-700 dark:text-amber-300">
              WhatsApp Calling is not enabled. Contact your admin to enable it.
            </div>
          )}

          {/* Enable/Disable toggle for admins (hide when pre-requisites not met) */}
          {isAdmin && !error?.note && (
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Voice Calling</span>
              </div>
              <Button
                size="sm"
                variant={callingEnabled ? 'outline' : 'default'}
                onClick={handleToggleCalling}
                disabled={enabling || success}
                className={callingEnabled ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30' : 'bg-green-600 hover:bg-green-700 text-white'}
              >
                {enabling ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : null}
                {callingEnabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          )}

          {/* Advanced settings info — only for admins (hide when pre-requisites not met) */}
          {isAdmin && !error?.note && (
          <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2.5">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium">Advanced Settings</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              To configure call hours, country restrictions and other settings, go to WhatsApp Manager:
            </p>
            <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Click on <span className="font-medium text-foreground">Account tools</span> &gt; <span className="font-medium text-foreground">Phone numbers</span> panel</li>
              <li>Click the <span className="font-medium text-foreground">gear icon</span> next to your phone number</li>
              <li>Click the <span className="font-medium text-foreground">Calls</span> tab</li>
            </ol>
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-1"
              onClick={() => window.open(whatsappManagerUrl, '_blank')}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Open WhatsApp Manager
            </Button>
          </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
